import { useState, useEffect } from "react";
import { api } from "@/App";
import { CallScreen, IncomingCallOverlay } from "@/components/CallScreen";
import { startRingtone, stopRingtone } from "@/utils/ringtone";
import { initializeNativeCall, endNativeCall, isNativeCallPlatform } from "@/utils/nativeCall";

// Global incoming-call manager: polls everywhere the user is logged in
export const CallManager = () => {
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [autoAccept, setAutoAccept] = useState(null); // callId answered via CallKit

  // Native VoIP/CallKit bridge
  useEffect(() => {
    initializeNativeCall();
    const onAnswer = (e) => setAutoAccept(e.detail.callId);
    const onDecline = (e) => {
      setIncomingCall((prev) => (prev?.call_id === e.detail.callId ? null : prev));
    };
    window.addEventListener("ade-native-answer", onAnswer);
    window.addEventListener("ade-native-decline", onDecline);
    return () => {
      window.removeEventListener("ade-native-answer", onAnswer);
      window.removeEventListener("ade-native-decline", onDecline);
    };
  }, []);

  // Ring + vibrate while an incoming call is shown (skip on iOS: CallKit rings natively)
  useEffect(() => {
    if (incomingCall && !activeCall && !isNativeCallPlatform()) {
      startRingtone();
    } else {
      stopRingtone();
    }
    return () => stopRingtone();
  }, [incomingCall, activeCall]);

  useEffect(() => {
    const checkCalls = async () => {
      if (!localStorage.getItem("token")) return;
      try {
        const { data } = await api.get("/calls/incoming");
        setIncomingCall((prev) => {
          if (data.call && !activeCall) return prev?.call_id === data.call.call_id ? prev : data.call;
          return data.call ? prev : null;
        });
      } catch {}
    };
    checkCalls();
    const interval = setInterval(checkCalls, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall]);

  // Auto-accept when the user answered from the native CallKit screen
  useEffect(() => {
    if (autoAccept && incomingCall && incomingCall.call_id === autoAccept && !activeCall) {
      setActiveCall(incomingCall);
      setIncomingCall(null);
      setAutoAccept(null);
    }
  }, [autoAccept, incomingCall, activeCall]);

  return (
    <>
      {incomingCall && !activeCall && (
        <IncomingCallOverlay
          call={incomingCall}
          onAccept={() => {
            setActiveCall(incomingCall);
            setIncomingCall(null);
          }}
          onDecline={() => {
            api.post(`/calls/${incomingCall.call_id}/decline`).catch(() => {});
            endNativeCall(incomingCall.call_id);
            setIncomingCall(null);
          }}
        />
      )}
      {activeCall && (
        <CallScreen
          mode="incoming"
          callId={activeCall.call_id}
          offer={activeCall.offer}
          otherUser={activeCall.from_user}
          onClose={() => {
            endNativeCall(activeCall.call_id);
            setActiveCall(null);
          }}
        />
      )}
    </>
  );
};
